import { GetChannelsRequest_PagingKey_Kind } from "@daangn/idl/rocketchat/api/api";
import type {
  Article,
  BizAccount,
  BubbleGroup,
  Channel_EnabledFeature,
  Channel_Input,
  FilterID,
  Introduction,
  Notice,
  Promise as RocketchatPromiseType,
  ToolboxButton,
  UnreadCount,
  User,
} from "@daangn/idl/rocketchat/api/model";
import { Badge, Channel_Type } from "@daangn/idl/rocketchat/api/model";
import { Entity } from "@data-client/endpoint";

import { addDays } from "date-fns";
import { ChatMessage } from "../chatMessage/chatMessage.entity";
import type { ChannelID, ChatMessageID, ResourceType, UserID } from "../types";
import { User as UserEntity } from "../user/user.entity";
import { formatRocketchatTimestamp } from "../utils/formatRocketchatTimestamp";

export const defaultPagingKey = {
  kind: GetChannelsRequest_PagingKey_Kind.KIND_FAVORITE,
  /**
   * @see https://daangn.slack.com/archives/CHUES2ZEE/p1749796176760339?thread_ts=1744772521.521339&cid=CHUES2ZEE
   */
  time: formatRocketchatTimestamp(addDays(Date.now(), 1)),
};

export type ChannelAggregationData = {
  unreadCount: UnreadCount;
  nextPagingKey: {
    kind: GetChannelsRequest_PagingKey_Kind;
    time: string;
  };
  filterId: FilterID;
};

export const channelResourceType: Record<string, ResourceType> = {
  UNSPECIFIED: "UNSPECIFIED",
  FLEA: "FLEA",
  FLBD: "FLBD",
  BUSI: "BUSI",
  STOR: "STOR",
  BIZA: "BIZA",
  GIGD: "GIGD",
  RLTY: "RLTY",
  JOBS: "JOBS",
  CARS: "CARS",
  MEET: "MEET",
  CBUY: "CBUY",
  MINI: "MINI",
};

export class Channel extends Entity {
  /**
   * The ID of the channel
   * ID를 파싱해서 안의 데이터를 쓰면 안된다.
   */
  id: ChannelID = "";
  /**
   * 게시글의 타입
   * 알파벳 4글자로 구성되며 아래에 정의된 타입만 사용된다.
   * FLEA(중고거래), FLBD(중고거래 번들), BUSI(동네홍보), STOR(동네생활), BIZA(비즈프로필),
   * GIGD(긱딜리버리), RLTY(부동산), JOBS(구인구직), CARS(중고차),
   * MEET(동네모임), CBUY(같이사요), MINI(미니앱)
   * QNAA(Deprecated), BULL(Deprecated), POPA(Deprecated), SMBA(Deprecated)
   */
  resource_type: ResourceType = channelResourceType.UNSPECIFIED;
  /** The ID of the resource */
  resource_id = "";
  /** 유저가 channel을 즐겨찾기 했으면 true. */
  is_favorite = false;
  /**
   * Read only channel이면 true
   * 채팅방의 속성이 read only일 경우 input을 자주 묻는 질문 보기로 세팅해 반환
   */
  is_read_only = false;
  /** Channel이 disable되어 있으면 true */
  is_disabled = false;
  /** 사용자가 channel의 알림을 껏으면 true */
  is_mute = false;
  /** Channel의 읽지 않은 메시지 수. */
  unread_count = 0;
  /** 사용자가 마지막으로 본 message_id */
  last_seen_id = "";
  /** 마지막으로 message가 보내진 시간(받은 것/보낸 것 둘다 포함) */
  last_message_time?: string | undefined = undefined;
  /** Channel 생성된 시간 */
  create_time?: string | undefined = undefined;
  /** 마지막 messgae */
  last_message?: ChatMessageID | undefined = undefined;
  /** 받는 사람의 정보. */
  receiver?: UserID | undefined = undefined;
  /** Channel에 묶인 article 정보 */
  article?: Article | undefined = undefined;
  /** 약속 데이터 */
  promise?: RocketchatPromiseType | undefined = undefined;
  /** 채널 안내 message */
  notice?: Notice | undefined = undefined;
  /** 리뷰 버튼 보여주기 여부. true면 버튼을 노출. */
  is_reviewable = false;
  /** 채팅방에서 사용가능한 기능 */
  enabled_feature?: Channel_EnabledFeature | undefined = undefined;
  /** 내 정보 */
  me?: UserID | undefined = undefined;
  /** 비즈니스 계정 정보 */
  biz_account?: BizAccount | undefined = undefined;
  /** RPC only. 채팅방 랜딩을 위한 당근 앱 내 URI(Deep link) */
  target_uri = "";
  /** 채널의 타입 */
  type = Channel_Type.TYPE_UNSPECIFIED;
  /** 채널의 Owner 권한을 가진 유저 */
  owner?: User | undefined = undefined;
  /** 채팅방 이름 */
  title = "";
  /** 채팅방 썸네일 */
  thumbnail = "";
  /**
   * GetChannel, UpdateUserChannel 호출 시에만 포함됨.
   * 채팅방의 is_disabled 속성이 true일 경우 Text{is_disabled: true, placeholder: "상대방과 대화가 불가능합니다."} 형태로
   * input 생성해 클라이언트에 전달.
   * 채팅방이 읽기 전용일 경우 커스텀 입력 창 정보가 있어도 자주 묻는 질문 보기 형태로 input 생성해 클라이언트에 전달.
   * input이 세팅되지 않으면 기본 텍스트 입력 창을 표시
   */
  input?: Channel_Input | undefined = undefined;
  /** 버블 메시지 그룹 */
  bubble_group?: BubbleGroup | undefined = undefined;
  /**
   * 소개 창
   * CreateChannel, GetChannel, UpdateUserChannel 호출 시에만 포함됨.
   */
  introduction?: Introduction | undefined = undefined;
  /** 툴박스에 들어가는 버튼 목록 */
  toolbox_buttons: ToolboxButton[] = [];
  /**
   * Channel이 생성되어있는지 여부를 나타낸다.
   * 아직까지 대화를 한적이 없다면 이 값이 true로 리턴된다.
   */
  is_temporary = false;
  /** 중요 채팅방으로 등록된 시간 */
  favorite_time?: string | undefined = undefined;
  /** 읽지 않은 멘션 수 */
  unread_mentions_count = 0;
  /** 채팅방에 참여하고 있는 인원 수 */
  member_count = 0;
  /** 읽지 않은 답장 수 */
  unread_reply_count = 0;
  /** 채널에 적용할 공통 필터 */
  common_filter_id?: FilterID | undefined = undefined;
  /** 채널에 적용할 서비스 필터 */
  service_filter_id?: FilterID | undefined = undefined;
  /**
   * 채팅 목록 뷰타입
   * 채팅방 대표 프로필 이미지
   */
  profile_image_urls: string[] = [];
  /** 채팅방 대표 이미지 위에 그려주는 문구 */
  cover_image_text?: string | undefined = undefined;
  /** 채팅방 대표 커버 이미지 */
  cover_image_url?: string | undefined = undefined;
  /** 제목 왼쪽에 표시되는 아이콘 URL */
  resource_icon_url?: string | undefined = undefined;
  /** 마지막 메시지 ID */
  last_message_id = "";
  /** 채널 뱃지 */
  badge = Badge.BADGE_UNSPECIFIED;
  /** 상호간의 메시지를 주고받았는지 여부 */
  has_mutual_messages = false;

  static key = "Channel";

  static schema = {
    last_message: ChatMessage,
    me: UserEntity,
    receiver: UserEntity,
  };

  static process(value: Channel, parent: Entity, key: string, args: unknown[]) {
    // biome-ignore lint/complexity/noThisInStatic:
    const processed = super.process(value, parent, key, args);
    return {
      ...Channel.defaults,
      ...processed,
    };
  }
}
